<template>
  <v-app>
    <div v-if="isLogin || consent">
      <sn-gradient-header
        :overlay-radius="24"
        :hide-back-btn="true"
        class="gradient-header"
        :class="($vuetify.breakpoint.smAndDown) ? 'sm' : ''"
      >
        <div class="pt-6 pt-sm-10 pb-5 px-4 px-sm-14">
          <br>
        </div>
      </sn-gradient-header>
      <login v-if="isLogin" />
      <consent
        v-else-if="consent"
      />
    </div>
    <no-access
      v-else-if="noAccess"
    />
    <div
      v-else-if="isManager"
      class="app-container"
      :style="appContainerStyle"
    >
      <sn-banner
        v-if="showBanner"
        :message="bannerText"
        type="warning"
        :primary-text="maySwitchToAdmin ? 'Switch to Admin view only' : ''"
        :secondary-text="maySwitchToProduction ? 'Switch to production' : ''"
        @primary-click="becomeSnAdmin"
        @secondary-click="toggleIsStaging"
      />
      <div class="flex sr-container">
        <sn-side-menu
          ref="sideMenu"
          :header-image="ncinoLogo"
          header-image-link="/manager"
          subheader-text=""
          :disable-sn-theme-mixin="true"
          :items="sideNavOptions"
          :width="navWidth"
          :permanent="true"
          :brand-colors="{}"
          link-type="to"
          class="side-menu"
        >
          <template
            v-if="showViewingAs"
            v-slot:footer
          >
            <viewing-as ref="viewingAs" />
          </template>
        </sn-side-menu>
        <div class="content sr-content">
          <sn-header
            :key="headerValue"
            :header-text="headerText"
            :subtitle-text="subtitleText"
            :breadcrumb-items="breadcrumbs"
            :is-loading="$store.getters.showHeaderLoaderSkeleton"
            :tab-list="headerTabs"
            :activated-tab="activeTabIndex"
            data-cy="header"
            @update-tab="toggleTabs"
          >
            <template v-slot:avatars>
              <sn-avatar
                v-if="userInitials !== ''"
                :image="user.profilePictureUrl"
                :initials="userInitials"
                class="mx-2"
                pointer-cursor
                data-cy="profile-icon"
                @click="showProfileDropdown()"
              />
              <profile-dropdown
                v-show="profileDropdownVisible"
                :key="orgId"
                :is-shown="profileDropdownVisible"
                :user-name="userFullName"
                :user-guid="user.guid"
                :profile-picture-url="user.profilePictureUrl"
                @change="resetAppTimeout"
                @close="closeProfileDropdown"
                @openProfileSettings="toggleProfileSettingsVisible"
                @logout="logout"
              />
            </template>
          </sn-header>
          <router-view
            v-if="!loading"
            :key="routerKey"
            class="page-content"
            @change="resetAppTimeout"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <router-view />
    </div>
    <my-profile-nav-dialog
      v-model="profileSettingsVisible"
      :initial-tab="initialTab"
      :user-guid="user.guid"
    />
    <sr-session-timeout-modal
      v-if="!isLogin"
      :display="timeoutModalVisible"
      :total-seconds="timeoutModalTotalSeconds"
      @extend="() => timeoutModalTotalSeconds = undefined"
    />
    <sn-toaster
      v-show="toastMixin_activeToasts.length > 0"
      :toasts="toastMixin_activeToasts"
      class="toast-container"
      @actionClicked="handleToastAction"
    />
    <sr-error-overflow-modal
      :toggle-modal="showErrorOverflowModal"
      :title="errorOverflowModalTitle"
      :message="errorOverflow"
      @close="toggleErrorOverflowModal"
    />
  </v-app>
</template>

<script>
import ncinoLogo from '../../assets/images/NcinoEsign.svg'
import snSigningRoomService from '../services/snSigningRoomService'
import brandMixin from './mixins/brandMixin.js'
import toastMixin from './mixins/toastMixin'
import { user, company, routeOrg } from './graphql/signing_room_queries.gql'
import { mapGetters, mapMutations } from 'vuex'
import cookie from 'js-cookie'
import _ from 'lodash'
import ProfileDropdown from './srui/profileDropdown.vue'
import Login from './login'
import NoAccess from './noAccess'
import consent from './consent.vue'
import MyProfileNavDialog from './srui/myProfileNavDialog'
import srSessionTimeoutModal from './srui/srSessionTimeoutModal'
import ViewingAs from './srui/srViewingAs'
import srErrorOverflowModal from './srui/srErrorOverflowModal'
import axios from 'axios'
import brandThemeMixin from '~/components/common/brandThemeMixin'

export default {
  name: 'SigningRoomApp',
  components: {
    srSessionTimeoutModal,
    ProfileDropdown,
    Login,
    NoAccess,
    consent,
    ViewingAs,
    srErrorOverflowModal,
    MyProfileNavDialog
  },
  mixins: [toastMixin, brandMixin, brandThemeMixin],
  data () {
    return {
      headerValue: 0,
      navWidth: 256,
      timeout: null,
      activeTabIndex: 0,
      loading: false,
      profileDropdownVisible: false,
      profileSettingsVisible: false,
      showViewingAs: true,
      tabActiveListener: null,
      windowResizeListener: null,
      trueViewingAsHeight: null,
      tabInactiveTimestamp: new Date().getTime(),
      timeoutModalTotalSeconds: undefined,
      errorOverflow: '',
      errorOverflowModalTitle: '',
      showErrorOverflowModal: false,
      initialTab: 0,
      showNotificationDropdown: false,
      showSnoozeModal: false,
      isSnAdmin: null,
      homeLink: '/',
      ncinoLogo,
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'isStaging',
      'activeHeaderTab',
      'viewAsCompany',
      'appInitialized',
      'orgId',
      'timeoutModalVisible',
      'viewAsCompany',
      'profileSettingsInitialTab'
    ]),
    isRoot () {
      return this.$route.path === '/'
    },
    isLogin () {
      return this.$route.path.includes('login')
    },
    isPortal () {
      return this.$route.path.includes('portal')
    },
    noAccess () {
      return this.$route.path.includes('no_access')
    },
    consent () {
      return this.$route.path.includes('consent')
    },
    isManager () {
      return this.$route.path.includes('manager')
    },
    sideNavOptions () {
      const options = []
      const hasSessionsOption = (!this.isSnAdmin && snSigningRoomService.hasPermission(this.user, 'manageSigningSessions')) || (this.viewAsCompany && this.viewAsCompany.guid)
      if (hasSessionsOption) {
        options.push(
          {
            title: 'Signing sessions',
            icon: '$snDocumentSign',
            link: '/manager/sessions'
          }
        )
      }

      const hasSettingsOption = (!this.isSnAdmin && snSigningRoomService.canAccessSettings(this.user)) || (this.viewAsCompany && this.viewAsCompany.guid)
      if (hasSettingsOption) {
        options.push(
          {
            title: 'Settings',
            icon: '$snSettings',
            link: '/manager/settings'
          }
        )
      }

      // const hasReportsOption = (!this.isSnAdmin && snSigningRoomService.hasPermission(this.user, 'manageReports')) || (this.viewAsCompany && this.viewAsCompany.guid)
      // if (hasReportsOption) {
      //   options.push(
      //     {
      //       title: 'Reports',
      //       icon: '$snDocuments',
      //       link: '/manager/reports'
      //     }
      //   )
      // }

      if (this.isSnAdmin) {
        if (options.length) {
          options.push({ type: 'divider' })
        }

        options.push(
          {
            title: 'Companies',
            icon: '$snCity',
            link: '/manager/companies'
          })

        options.push(
          {
            title: 'API Integrations',
            icon: '$snLinkVariant',
            link: '/manager/integrations'
          }
        )

        const { host } = window.location
        const devEnvs = ['localhost', 'ncmstaging.com', 'snstaging.com', 'ngrok']
        if (devEnvs.some(env => host.includes(env))) {
          options.push({
            title: 'Dev tools',
            icon: '$snTools',
            link: '/manager/dev_tools'
          })
        }
      }
      return options
    },
    userFullName () {
      if (this.user) {
        return `${this.user.firstName || ''} ${this.user.lastName || ''}`
      }
      return ''
    },
    userInitials () {
      if (this.user) {
        const { firstName, lastName } = this.user
        return `${firstName ? firstName[0] : ''}${lastName ? lastName[0] : ''}`.toUpperCase()
      }
      return ''
    },
    headerText () {
      if (this.isRoot) {
        this.setHeaderText('Welcome to your nCino eSign')
      }
      return this.$store.getters.headerText
    },
    subtitleText () {
      if (this.isRoot) {
        this.setSubtitleText('Welcome to nCino eSign')
      }
      return this.$store.getters.subtitleText
    },
    breadcrumbs () {
      if (this.isRoot) this.setBreadcrumbs([])
      return this.$store.getters.breadcrumbs
    },
    headerTabs () {
      if (this.isRoot) this.setHeaderTabs([])
      return this.$store.getters.headerTabs
    },
    routerKey () {
      if (this.orgId != null && this.orgId !== 'undefined' && this.orgId !== '') {
        return this.orgId + this.isStaging
      }
      return this.isStaging
    },
    showBanner () {
      return this.isStaging || this.viewAsCompany
    },
    bannerText () {
      if (this.isStaging && !this.viewAsCompany) {
        return 'You\'re in the Staging environment'
      } else if (this.isStaging) {
        return `You're viewing as ${this.viewAsCompany.name} ${this.viewAsCompany.locked && this.isSnAdmin ? '(Locked out)' : ''} in the Staging environment`
      } else if (this.viewAsCompany) {
        return `You're viewing as ${this.viewAsCompany.name} ${this.viewAsCompany.locked && this.isSnAdmin ? '(Locked out)' : ''}`
      }
      return ''
    },
    maySwitchToAdmin () {
      return this.viewAsCompany
    },
    maySwitchToProduction () {
      return this.isStaging && snSigningRoomService.hasPermission(this.user, 'productionEnabled')
    },
    appContainerStyle () {
      if (this.showBanner) {
        return {
          top: '72px !important',
          height: 'calc(100% - 72px)'
        }
      }
      return undefined
    }
  },
  watch: {
    $route (val) {
      if (!this.appInitialized) {
        return
      }
      if (this.user != null && this.user.isSnAdmin && val.fullPath.includes('manager') && !this.viewAsCompany) {
        this.setOrgId(null) // un-applies org branding coming back to manager from portal for sn admins
      }
      this.checkAccess()
      if (cookie.get('tmp_session_guid') != null) {
        this.$router.push(`/manager/sessions/${cookie.get('tmp_session_guid')}`)
        cookie.remove('tmp_session_guid')
      }
      this.resetAppTimeout()
    },
    headerTabs () {
      this.headerValue++
    },
    activeHeaderTab () {
      this.activeTabIndex = this.headerTabs.map(tab => tab.key).indexOf(this.activeHeaderTab)
    }
  },
  async created () {
    if (this.isLogin || this.isPortal) {
      return
    }

    this.loading = true
    const user = await this.getUser()

    // 'visibililtychange' listens for when browser tab becomes (in)active
    this.tabActiveListener = document.addEventListener('visibilitychange', this.handleTabVisibilityChange)
    this.windowResizeListener = window.addEventListener('resize', this.handleWindowResize)

    if (!user) {
      return
    }

    const { guid, firstName, lastName, locked, profilePictureUrl, timezone, rawPermissions, company } = user
    const permissions = this.camelizePermissions(JSON.parse(rawPermissions) || {})
    const companyLocked = company.locked
    const featureFlags = JSON.parse(company.featureFlags) || {}

    const isSnAdmin = this.isSnAdmin
    this.setUser({ ...this.user, guid, firstName, lastName, locked, profilePictureUrl, timezone, isSnAdmin, permissions, company, companyLocked, featureFlags })
    await this.loadViewAsCompany()

    if (cookie.get('temp_staging') != null) {
      this.setIsStaging(cookie.get('temp_staging') === true || cookie.get('temp_staging') === 'true')
      cookie.remove('temp_staging')
    } else if (isSnAdmin && !this.viewAsCompany) {
      this.setIsStaging(this.getDefaultStaging())
    }

    const requiresOrg = snSigningRoomService.requiresOrg(this.$route.name)
    if (requiresOrg && !this.mersOrgId) {
      // if the route requires an org and you don't have one selected, find the org and select it
      await this.getRouteOrg()
    }

    this.checkAccess()

    if (cookie.get('tmp_session_guid') != null) {
      this.$router.push(`/manager/sessions/${cookie.get('tmp_session_guid')}`)
      cookie.remove('tmp_session_guid')
    }

    setTimeout(() => {
      this.loading = false
      this.setAppInitialized(true)
    }, 500)

    this.resetAppTimeout()
  },
  beforeDestroy () {
    document.removeEventListener('visibilitychange', this.tabActiveListener)
    window.removeEventListener('resize', this.windowResizeListener)
  },
  methods: {
    ...mapMutations([
      'setHeaderText',
      'setBreadcrumbs',
      'setHeaderTabs',
      'setIsStaging',
      'setUser',
      'setActiveHeaderTab',
      'becomeCompany',
      'setAppInitialized',
      'setOrgId',
      'resetAppTimeout',
      'setSubtitleText',
      'setTimeoutModalVisible'
    ]),

    async getRouteOrg () {
      const { data } = await this.$apollo.query({
        query: routeOrg,
        variables: {
          route: this.$route.name,
          guid: this.$route.params.guid
        }
      })

      if (data.routeOrg) {
        this.setOrgId(data.routeOrg.guid)
        this.setIsStaging(data.routeOrg.isTest)
        if (this.isSnAdmin && data.routeOrg.company != null) {
          this.becomeCompany(data.routeOrg.company)
        }
      }
    },

    checkAccess () {
      if (this.user.loading) {
        return
      }

      if (this.$route.name != null) {
        const canAccess = snSigningRoomService.canAccess(this.user, this.$route.name)
        if (!canAccess) {
          this.$router.push(snSigningRoomService.getDefaultPage(this.user))
        }
      }
    },

    async getUser () {
      const { data } = await this.$apollo.query({
        query: user,
        variables: {}
      })

      if (data.user == null) {
        this.setUser({ loggedIn: false, permissions: {} })
      } else {
        this.isSnAdmin = data.user.admin
        this.setUser({ ...data.user, permissions: {}, loggedIn: true })
      }
      return data.user
    },

    toggleTabs (tabId) {
      this.activeTabIndex = this.headerTabs.map(tab => tab.id).indexOf(tabId)
      const tab = this.headerTabs[this.activeTabIndex]
      this.setActiveHeaderTab(tab.key)
      if (tab.breadcrumb != null) {
        const breadcrumbs = this.$store.getters.breadcrumbs
        if (breadcrumbs != null && breadcrumbs.length > 1) {
          breadcrumbs.pop()
        }
        breadcrumbs.push({ text: tab.breadcrumb })
        this.setBreadcrumbs(breadcrumbs)
      }
    },

    async loadViewAsCompany () {
      if (!this.isSnAdmin) {
        this.becomeCompany(null)
        return null
      }
      // Using a cookie so that when user opens a new tab they still have a way to load the company guid. For example, link in monolith.
      const viewAsCompanyGuid = sessionStorage.getItem('srm_view_as_company_guid') || cookie.get('tmp_view_as_company_guid')
      cookie.remove('tmp_view_as_company_guid')

      if (viewAsCompanyGuid) {
        const { data } = await this.$apollo.query({
          query: company,
          variables: {
            companyGuid: viewAsCompanyGuid
          }
        })
        this.becomeCompany(data.company)

        const orgId = sessionStorage.getItem('srm_org_id') || cookie.get('temp_org_guid') || cookie.get('srm_org_id')
        cookie.remove('temp_org_guid')
        this.setOrgId(orgId)
      }
    },

    camelizePermissions (permissions) {
      /* camelCase and trim permission keys:
      permission_setting_change_status_and_change_data => changeStatusAndChangeData
      access like: this.user.permissions.changeStatusAndChangeData */
      Object.keys(permissions).forEach(key => {
        const cleanKey = _.camelCase(key.replace('permission_setting_', ''))
        permissions[cleanKey] = permissions[key]
        delete permissions[key]
      })
      return permissions
    },

    showProfileDropdown () {
      this.resetAppTimeout()
      if (!this.profileDropdownVisible) {
        this.profileDropdownVisible = true
      }
    },

    closeProfileDropdown () {
      this.resetAppTimeout()
      this.showNotificationDropdown = false
      this.profileDropdownVisible = false
    },

    toggleIsStaging () {
      const matchedPath = this.$route.matched[0].path
      const basePath = matchedPath.split(':')[0]
      this.setIsStaging(!this.isStaging)

      if (basePath !== matchedPath) {
        this.$router.push(basePath)
      }
    },

    toggleProfileSettingsVisible () {
      this.profileSettingsVisible = !this.profileSettingsVisible
      snSigningRoomService.toggleVerticalScrollbar(this.profileSettingsVisible)
    },

    getDefaultStaging () {
      return process.env.RAILS_ENV === 'development' || process.env.RAILS_ENV === 'staging'
    },

    becomeSnAdmin () {
      this.$router.push('/manager/companies').then(() => {
        this.becomeCompany(null)
        this.setIsStaging(this.getDefaultStaging())
        this.setOrgId(null)
      })
    },

    logout () {
      if (this.isPortal) {
        sessionStorage.clear()
        window.location.assign('/portal/logged_out')
      } else {
        sessionStorage.clear()
        window.location.assign('/logout')
      }
    },

    async handleTabVisibilityChange () {
      if (document.visibilityState === 'visible') {
        const authIsValid = await this.getAuthStatus()
        if (!authIsValid && !this.timeoutModalVisible) {
          this.logout()
        }

        const now = new Date().getTime()
        const minutesInactive = (now - this.tabInactiveTimestamp) / 60000

        if (minutesInactive > 15) {
          if (!this.timeoutModalVisible) {
            this.logout()
          }
        } else if (minutesInactive > 10) {
          this.timeoutModalTotalSeconds = Math.floor((15 - minutesInactive) * 60)
          this.setTimeoutModalVisible(true)
        } else {
          const minutesUntilModalShows = 10 - minutesInactive
          this.resetAppTimeout(minutesUntilModalShows)
        }
      } else {
        this.tabInactiveTimestamp = new Date().getTime()
      }
    },

    handleWindowResize () {
      if (this.isPortal) {
        return
      }
      const fullSideMenu = this.$refs.sideMenu.$el
      const viewingAs = this.$refs.viewingAs?.$el
      if (viewingAs) {
        // trueViewingAsHeight is the height of the viewingAs element if it's ever rendered.
        // we still get into this block if the viewingAs element is hidden, so we save the height while we have it
        // so that we can determine if we have enough room to show it again
        this.trueViewingAsHeight = viewingAs.clientHeight
      }

      const viewingAsHeight = viewingAs?.clientHeight || this.trueViewingAsHeight || 130
      const fullSideMenuHeight = fullSideMenu.clientHeight
      const spaceForItems = this.sideNavOptions.length * 65

      if (fullSideMenuHeight - spaceForItems < viewingAsHeight) {
        this.showViewingAs = false
      } else {
        this.showViewingAs = true
      }
    },

    async getAuthStatus () {
      const authGuid = cookie.get('srm_auth_guid')
      const { data } = await axios.post('/auth/v1/sync', { auth_guid: authGuid })
      return data.success
    },

    handleToastAction (data) {
      switch (data.dataSource) {
        case 'errorOverflow':
          this.errorOverflow = data.data
          this.errorOverflowModalTitle = data.title
          this.toggleErrorOverflowModal()
          break
        default:
          break
      }
    },

    toggleErrorOverflowModal () {
      this.showErrorOverflowModal = !this.showErrorOverflowModal
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~/assets/css/snui/index';
main {
  background-color: white;
}

.content {
  width: 100vw;
}

.page-content {
  z-index: 100;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: auto;
  overscroll-behavior: none;
}

.flex {
  display: flex;
}

.gradient-header.sm {
  height: 80px !important;
}
.gradient-header:not(.sm) {
  height: 230px !important;
}

.sr-container {
  height: 100%;
  overflow: hidden;
}

.sr-content {
  overflow: auto;
}

</style>
